import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { FilterConditionOutput } from '@components/ui/AdvancedFilter';
import { TotalResults } from '@shared/components/ui/TotalResults';
import { type AgGridTableProps } from '@shared/components/ui/AgGridTable';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useStockMovements } from '../../hooks/useStockMovements';
import { ErrorState, LoadingState } from '../feedback';
import { CardList, StockMovementsAgGridTable } from '../display';
import { FiltersSection } from '../interaction';
import { DownloadExcelButton } from '../interaction';
import { EmptyView, EMPTY_VIEW_ICONS } from '@shared/components/ui/EmptyView';
import { useProcessedFilters } from '../../hooks/useProcessedFilters';

type SortingConfig = NonNullable<AgGridTableProps['sorting']>;

export const Main = () => {
  const { t } = useTranslation('stockmovementsModule');
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // 🎯 Hook useQueryParams para sincronizar con URL
  const { params: queryParams, updateParams: updateQueryParams } = useQueryParams({
    search: { type: 'string', default: '' },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
    sortBy: { type: 'string', default: 'id' },
    sortDirection: { type: 'string', default: 'desc' },
  });

  // 🎯 Estados locales sincronizados con URL
  const [searchTerm, setSearchTerm] = useState(queryParams.search ?? '');
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(
    Array.isArray(queryParams.filters) ? queryParams.filters : [],
  );
  const [sorting, setSorting] = useState<SortingConfig | null>(() => ({
    field: queryParams.sortBy || 'id',
    direction: (queryParams.sortDirection as 'asc' | 'desc') || 'desc',
  }));

  // 🎯 Efectos para sincronizar estados locales con URL
  useEffect(() => {
    setSearchTerm(queryParams.search ?? '');
  }, [queryParams.search]);

  useEffect(() => {
    const nextFilters = Array.isArray(queryParams.filters) ? queryParams.filters : [];
    // 🎯 Comparar serializados para evitar actualizaciones innecesarias
    setFilterConditions((prev) => {
      const prevSerialized = JSON.stringify(prev);
      const nextSerialized = JSON.stringify(nextFilters);
      return prevSerialized === nextSerialized ? prev : nextFilters;
    });
  }, [queryParams.filters]);

  useEffect(() => {
    const nextSorting = {
      field: queryParams.sortBy || 'id',
      direction: (queryParams.sortDirection as 'asc' | 'desc') || 'desc',
    };
    setSorting((prev) => {
      if (prev && prev.field === nextSorting.field && prev.direction === nextSorting.direction) {
        return prev;
      }
      return nextSorting;
    });
  }, [queryParams.sortBy, queryParams.sortDirection]);

  // 🎯 Handlers optimizados para evitar bucles infinitos
  const handleSearch = useCallback(
    (searchValue: string) => {
      setSearchTerm(searchValue);
      if (queryParams.search !== searchValue) {
        updateQueryParams({ search: searchValue });
      }
    },
    [updateQueryParams, queryParams.search],
  );

  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      setFilterConditions(conditions);
      const currentFilters = Array.isArray(queryParams.filters) ? queryParams.filters : [];
      if (JSON.stringify(currentFilters) !== JSON.stringify(conditions)) {
        updateQueryParams({ filters: conditions });
      }
    },
    [updateQueryParams, queryParams.filters],
  );

  const handleSortingChange = useCallback(
    (newSorting: SortingConfig | null) => {
      setSorting(newSorting);
      const sortBy = newSorting?.field || 'id';
      const sortDirection = newSorting?.direction || 'desc';

      if (queryParams.sortBy !== sortBy || queryParams.sortDirection !== sortDirection) {
        updateQueryParams({
          sortBy: sortBy === 'id' ? undefined : sortBy,
          sortDirection: sortDirection === 'desc' ? undefined : sortDirection,
        });
      }
    },
    [updateQueryParams, queryParams.sortBy, queryParams.sortDirection],
  );

  const processedFilters = useProcessedFilters(filterConditions, sorting);

  const {
    stockmovements,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total,
  } = useStockMovements({
    searchTerm,
    ...processedFilters,
  });

  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  const stockmovementsViewProps = useMemo(
    () => ({
      stockmovements,
      isLoading: isInitialLoad && isLoading,
      isFetching: !isInitialLoad && isFetching,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
    }),
    [stockmovements, isInitialLoad, isLoading, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage, total],
  );

  const renderStockMovements = () => {
    if (isMobile) {
      return <CardList {...stockmovementsViewProps} showActions={true} />;
    }
    return (
      <StockMovementsAgGridTable {...stockmovementsViewProps} sorting={sorting} onSortingChange={handleSortingChange} />
    );
  };

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  if (isInitialLoad) {
    return <LoadingState message={t('loading.message')} />;
  }

  return (
    <div className="px-6 pt-4">
      <div className="mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <FiltersSection handleSearch={handleSearch} onFilterChange={handleFilterChange} />
            <DownloadExcelButton
              filters={{
                searchTerm,
                ...processedFilters,
              }}
            />
          </div>
          <TotalResults total={total} />
        </div>
      </div>

      {stockmovements.length > 0 && <div className="mb-4 mt-4">{renderStockMovements()}</div>}

      {stockmovements.length === 0 && (
        <EmptyView
          type={searchTerm || filterConditions.length > 0 ? 'noResults' : 'emptyState'}
          icon={EMPTY_VIEW_ICONS.STOCK_MOVEMENTS}
        />
      )}
    </div>
  );
};
